/**
 * AuditHistoryModal
 *
 * Displays audit trail / history logs for both SSS booking creation and TRS
 * time entry submission for one engagement, plus a "Show in Engagement
 * View" quick-action link.
 */
import { ExternalLink, X } from 'lucide-react';
import { useAuditHistory } from '../../hooks/useWipComparison.js';

/**
 * @typedef {object} AuditHistoryEntry
 * @property {string} id
 * @property {'sss'|'trs'} source
 * @property {string} description
 * @property {string} actor
 * @property {string} occurredAt
 */

/**
 * @param {{
 *   engagementId: string|null,
 *   engagementName?: string,
 *   onClose: () => void,
 *   onShowInEngagementView: () => void,
 * }} props
 */
export const AuditHistoryModal = ({ engagementId, engagementName, onClose, onShowInEngagementView }) => {
  const { data, isLoading } = useAuditHistory(engagementId);
  const entries = /** @type {AuditHistoryEntry[]} */ (data?.entries ?? []);

  if (!engagementId) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-2xl rounded-lg border border-edge bg-base shadow-2xl">
        <div className="flex items-center justify-between border-b border-edge px-5 py-4">
          <div>
            <h2 className="text-base font-semibold text-primary">Audit History</h2>
            {engagementName && <p className="text-sm text-muted">{engagementName}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted hover:bg-surface-hover hover:text-primary"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="max-h-96 overflow-y-auto px-5 py-4">
          {isLoading && (
            <div className="space-y-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-10 animate-pulse rounded bg-surface" />
              ))}
            </div>
          )}

          {!isLoading && entries.length === 0 && (
            <p className="py-6 text-center text-sm text-muted">No history logs found.</p>
          )}

          {!isLoading &&
            entries.map((entry) => (
              <div key={entry.id} className="flex gap-3 border-b border-edge py-3 last:border-0">
                <span
                  className={`mt-0.5 h-fit rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${
                    entry.source === 'sss'
                      ? 'bg-accent/15 text-accent'
                      : 'bg-emerald-500/15 text-emerald-400'
                  }`}
                >
                  {entry.source === 'sss' ? 'SSS' : 'TRS'}
                </span>
                <div>
                  <p className="text-sm text-primary">{entry.description}</p>
                  <p className="text-xs text-muted">
                    {entry.actor} &middot; {new Date(entry.occurredAt).toLocaleString()}
                  </p>
                </div>
              </div>
            ))}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-edge px-5 py-3">
          <button
            type="button"
            onClick={onShowInEngagementView}
            className="flex items-center gap-1.5 rounded-md border border-edge px-3 py-1.5 text-sm text-primary hover:bg-surface-hover"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            Show in Engagement View
          </button>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md bg-accent px-3 py-1.5 text-sm text-white hover:bg-accent-hover"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
